/**
 * Help Video Tour registry.
 * Prefer curated scene overrides; otherwise auto-build from help narrations.
 */

import { getHelpNarration, HELP_NARRATIONS } from '../../common/helpNarrations';
import { buildScenesFromNarration } from './buildScenes';

export const PIPING_WALKTHROUGH_GUIDE_ID = 'piping-progress-walkthrough';
const PIPING_ACCENT = '#0369a1';

export const GA_DASHBOARDS_GUIDE_ID = 'ga-dashboards-help';
const GA_ACCENT = '#0f766e';

export const TIME_TRACKER_GUIDE_ID = 'time-tracker-help';
export const TIME_TRACKER_ADMIN_GUIDE_ID = 'time-tracker-admin-help';
export const FAB_HISTOGRAM_GUIDE_ID = 'fab-histogram-help';
const TT_ACCENT = '#0d9488';
const TT_ADMIN_ACCENT = '#0f766e';
const FAB_ACCENT = '#0f766e';

/** Curated scenes for the piping new-employee tour (aligned with narration sections). */
export const PIPING_WALKTHROUGH_SCENES = [
  {
    id: 'welcome',
    title: 'Welcome to the piping tracker',
    icon: '🎬',
    blurb: 'What this tool is, and why every edit you make matters.',
    callouts: [
      { label: 'Shop + field progress', x: 18, y: 28 },
      { label: 'Feeds every earned report', x: 62, y: 55 },
    ],
    frame: 'hero',
  },
  {
    id: 'find-it',
    title: 'Finding Piping in the portal',
    icon: '🧭',
    blurb: 'Pick a project, open Progress Tracking, then Piping.',
    callouts: [
      { label: '1. Project dropdown', x: 42, y: 12 },
      { label: '2. Progress Tracking → Piping', x: 14, y: 48 },
    ],
    frame: 'sidebar',
  },
  {
    id: 'matrix',
    title: 'The matrix at a glance',
    icon: '📊',
    blurb: 'One row per ISO / line. Source columns, progress steps, and calculated earned values.',
    callouts: [
      { label: 'ISO / line identity', x: 12, y: 38 },
      { label: 'Progress steps', x: 48, y: 38 },
      { label: 'Auto earned totals', x: 78, y: 38 },
    ],
    frame: 'matrix',
  },
  {
    id: 'freeze',
    title: 'Freeze Rules of Credit first',
    icon: '🔒',
    blurb: 'Progress entry stays locked until weights are frozen for the job.',
    callouts: [
      { label: 'Rules of Credit button', x: 72, y: 14 },
      { label: 'Freeze for project', x: 55, y: 58 },
    ],
    frame: 'freeze',
  },
  {
    id: 'shop',
    title: 'Shop fabrication stages',
    icon: '🏭',
    blurb: 'Total Shop Completion moves through cut, bench, tack, weld, and ship.',
    callouts: [
      { label: '0 → 35 → 40 → 65 → 95 → 100', x: 50, y: 42 },
    ],
    frame: 'shop',
  },
  {
    id: 'field',
    title: 'Field steps and weighted completion',
    icon: '📐',
    blurb: 'Transport, supports, fit-up, bolt/weld, cleanup — weighted into Total Field Completion.',
    callouts: [
      { label: 'Step percents (0 / 25 / 50 / 75 / 100)', x: 35, y: 36 },
      { label: 'Weighted total', x: 78, y: 62 },
    ],
    frame: 'field',
  },
  {
    id: 'excel',
    title: 'Working like a spreadsheet',
    icon: '⚡',
    blurb: 'Inline edit, drag-fill, filters, column layout — remembered per project.',
    callouts: [
      { label: 'Drag-fill handle', x: 44, y: 52 },
      { label: 'Column filters', x: 28, y: 22 },
    ],
    frame: 'excel',
  },
  {
    id: 'import-export',
    title: 'Getting data in and out',
    icon: '📦',
    blurb: 'Excel import for source columns. Export what you see — or everything.',
    callouts: [
      { label: 'Import', x: 58, y: 12 },
      { label: 'Export', x: 68, y: 12 },
      { label: 'Progress never overwritten', x: 50, y: 58 },
    ],
    frame: 'io',
  },
  {
    id: 'history',
    title: 'Line history and trends',
    icon: '📈',
    blurb: 'Click a line tag to see how that ISO progressed over time.',
    callouts: [
      { label: 'Click the blue line tag', x: 16, y: 40 },
      { label: 'Progress trend chart', x: 62, y: 48 },
    ],
    frame: 'trends',
  },
  {
    id: 'holds',
    title: 'Holds, comments, and teammates',
    icon: '💬',
    blurb: 'On-hold flags, @mentions, and live presence while others edit.',
    callouts: [
      { label: 'On hold', x: 82, y: 36 },
      { label: 'Who is here', x: 88, y: 12 },
    ],
    frame: 'collab',
  },
  {
    id: 'schedule',
    title: 'Link lines to the schedule',
    icon: '🔗',
    blurb: 'Activity I D rolls tracker hours into Schedule Instances percent complete.',
    callouts: [
      { label: 'Activity I D column', x: 22, y: 40 },
      { label: 'Scheduler owns these codes', x: 60, y: 58 },
    ],
    frame: 'schedule',
  },
  {
    id: 'analytics',
    title: 'Analytics and downstream reports',
    icon: '📉',
    blurb: 'Tracker edits feed Earned, Budget, E V M, and S O V — live.',
    callouts: [
      { label: 'Analytics dashboard', x: 50, y: 40 },
      { label: 'Reports menu', x: 14, y: 70 },
    ],
    frame: 'analytics',
  },
  {
    id: 'sisters',
    title: 'NDE and Testing trackers',
    icon: '🧪',
    blurb: 'Sister views under the same Piping dropdown for welds and pressure tests.',
    callouts: [
      { label: 'Progress · NDE · Testing', x: 50, y: 16 },
    ],
    frame: 'sisters',
  },
  {
    id: 'reissue',
    title: 'When an ISO is re-issued',
    icon: '🔁',
    blurb: 'Old line stays struck-through; new Extra line carries the new quantities.',
    callouts: [
      { label: 'Superseded (read-only)', x: 30, y: 36 },
      { label: 'New active Extra line', x: 30, y: 58 },
    ],
    frame: 'reissue',
  },
  {
    id: 'first-week',
    title: 'Your first week on the job',
    icon: '✅',
    blurb: 'A short checklist so day-one edits are accurate and confident.',
    callouts: [
      { label: 'Confirm freeze → filter your area → update steps', x: 50, y: 48 },
    ],
    frame: 'checklist',
  },
];

/** Curated scenes for GA Dashboards (1:1 with ga-dashboards-help narration sections). */
export const GA_DASHBOARDS_SCENES = [
  {
    id: 'drawing-tells-time',
    title: 'A drawing that tells time',
    icon: '📐',
    blurb: 'Pins and balloons on a GA drawing show live P6 dates and labor units.',
    callouts: [
      { label: 'Site plan + status pins', x: 48, y: 48 },
      { label: 'Balloons: dates · % · Bud/Act/Rem', x: 12, y: 28 },
    ],
    frame: 'gaHero',
  },
  {
    id: 'rev-units',
    title: 'Rev, units, and how data gets in',
    icon: '🔁',
    blurb: 'Toolbar Rev reads live P6; balloons are P6-only — trackers do not feed GA.',
    callouts: [
      { label: 'Rev dropdown (live P6)', x: 38, y: 14 },
      { label: 'Compare · % mode', x: 58, y: 14 },
    ],
    frame: 'gaToolbar',
  },
  {
    id: 'setup-fast',
    title: 'Setting up fast',
    icon: '⚡',
    blurb: 'Edit mode: pin / zone / polygon, bind to WBS or activities, Show toggles, copy layout.',
    callouts: [
      { label: 'Pin · Zone · Polygon', x: 22, y: 18 },
      { label: 'Bind + Show toggles', x: 78, y: 42 },
    ],
    frame: 'gaEdit',
  },
  {
    id: 'balloons-colours',
    title: 'Balloons and colours',
    icon: '🎈',
    blurb: 'Leaders to gutters; border + fill colours; grey / amber / green / red-late; A-suffix for actuals.',
    callouts: [
      { label: 'Status legend', x: 50, y: 88 },
      { label: 'Leader to balloon', x: 30, y: 40 },
    ],
    frame: 'gaBalloons',
  },
  {
    id: 'presenting',
    title: 'Presenting like a pro',
    icon: '🖥',
    blurb: 'Present mode, week scrubber, PNG, and a PDF of every slide — header optional.',
    callouts: [
      { label: 'Week scrubber', x: 45, y: 88 },
      { label: 'PDF all slides', x: 88, y: 88 },
    ],
    frame: 'gaPresent',
  },
  {
    id: 'press-progress',
    title: 'Press Progress on seven five nine nine eight',
    icon: '📰',
    blurb: 'Companion Press Progress grid on job 75998 — same Rev idea as GA boards.',
    callouts: [
      { label: 'Press rows × disciplines', x: 50, y: 48 },
      { label: 'Rev · Current (live)', x: 82, y: 12 },
    ],
    frame: 'gaPress',
  },
];

/** Curated scenes for Time Tracker field help (1:1 with time-tracker-help narration). */
export const TIME_TRACKER_SCENES = [
  { id: 'welcome', title: 'Hours that payroll can trust', icon: '⏱️', blurb: 'Field hours become payroll and actuals. The labour code has to be right first.', callouts: [{ label: 'Labour code first', x: 28, y: 42 }, { label: 'Then hours', x: 68, y: 62 }], frame: 'ttHero' },
  { id: 'who', title: 'Who uses what', icon: '👥', blurb: 'Crew heads use Daily. Weekly is for project managers, accounting, and superadmin.', callouts: [{ label: 'Daily · crew', x: 24, y: 40 }, { label: 'Weekly · office', x: 76, y: 40 }], frame: 'ttDailyWeekly' },
  { id: 'log', title: 'The main hours log', icon: '📋', blurb: 'Search, filter, draft vs submitted. Delete only while the period is open.', callouts: [{ label: 'Draft vs submitted', x: 30, y: 28 }, { label: 'Search / filter', x: 72, y: 18 }], frame: 'matrix' },
  { id: 'perms', title: 'Permissions you will hit', icon: '🔐', blurb: 'Self, team, or all. Admin writes still need cost clerk or controller.', callouts: [{ label: 'Self · team · all', x: 28, y: 40 }, { label: 'Lock = 423', x: 74, y: 58 }], frame: 'freeze' },
  { id: 'where-codes', title: 'Where labour codes come from', icon: '📘', blurb: 'Maestro catalogue → Admin loads the job → dropdown. Time Tracker does not invent codes.', callouts: [{ label: '1. Maestro', x: 14, y: 38 }, { label: '2. Admin load', x: 38, y: 38 }, { label: '3. Dropdown', x: 62, y: 38 }, { label: '4. Hours save', x: 86, y: 38 }], frame: 'ttPipeline' },
  { id: 'job-disc-rule', title: 'Job plus discipline is the rule', icon: '🧭', blurb: 'Always pick the job, then the discipline. Job numbers ending in PIP already mean Piping.', callouts: [{ label: 'Job first', x: 24, y: 28 }, { label: 'Then PIP / MWR / ELE', x: 74, y: 58 }], frame: 'ttDropdown' },
  { id: 'ranges', title: 'Discipline ranges', icon: '🗺️', blurb: '21–29xxx labour, extras plus 60k, 01xxx indirects. Discipline equals trade.', callouts: [{ label: '21–29 labour', x: 24, y: 36 }, { label: '81–89 extras', x: 72, y: 36 }], frame: 'ttPipeline' },
  { id: 'picking', title: 'Picking a code on the timesheet', icon: '🔎', blurb: 'Pick 99999 PFS, then search. Extras show an amber badge. Switching project clears the code.', callouts: [{ label: 'Project-discipline first', x: 22, y: 16 }, { label: 'Amber Extra', x: 78, y: 62 }], frame: 'ttDropdown' },
  { id: 'daily', title: 'Daily crew timesheet', icon: '📅', blurb: 'Crew heads only. One date, multi-row, no draft. Submit sends it.', callouts: [{ label: 'Crew + date', x: 24, y: 28 }, { label: 'Submit, no draft', x: 74, y: 58 }], frame: 'ttDailyWeekly' },
  { id: 'daily-allow', title: 'Daily allowances', icon: '💵', blurb: 'R&B, meals, travel$ per employee. Split by hours. Pin a row to reserve an amount.', callouts: [{ label: 'Once per person', x: 26, y: 30 }, { label: 'Pin a row', x: 74, y: 58 }], frame: 'form' },
  { id: 'weekly', title: 'Weekly personal timesheet', icon: '🗓️', blurb: 'Canada Sun–Sat. United States Mon–Sun. One project + code across seven days.', callouts: [{ label: 'CA Sun–Sat', x: 24, y: 40 }, { label: 'US Mon–Sun', x: 76, y: 40 }], frame: 'ttDailyWeekly' },
  { id: 'weekly-grid', title: 'Weekly hours grid', icon: '🧮', blurb: 'Hours per day, shift D/A/N, comments as day-off notes on PDFs.', callouts: [{ label: 'Hours per day', x: 30, y: 36 }, { label: 'Shift + comments', x: 74, y: 58 }], frame: 'matrix' },
  { id: 'weekly-allow', title: 'Weekly allowances', icon: '🧾', blurb: 'Daily totals auto-distribute. Per-row breakdown is read-only.', callouts: [{ label: 'Daily totals', x: 26, y: 28 }, { label: 'Read-only split', x: 74, y: 58 }], frame: 'form' },
  { id: 'draft', title: 'Draft and submit', icon: '📝', blurb: 'Auto-save drafts. Submit validates. 24-hour cap. Locked period returns 423.', callouts: [{ label: 'Auto-save', x: 22, y: 28 }, { label: '24h / lock', x: 74, y: 58 }], frame: 'freeze' },
  { id: 'after-submit', title: 'After you submit', icon: '✅', blurb: 'Submitted rows feed PDFs and Maestro. After lock, call Accounting.', callouts: [{ label: 'Payroll', x: 18, y: 28 }, { label: 'PDFs', x: 50, y: 28 }, { label: 'Analytics', x: 82, y: 28 }], frame: 'ttSaved' },
  { id: 'crews', title: 'Crews from the field', icon: '👷', blurb: 'Must be a crew head. Admin assigns and transfers. No crew, no Daily.', callouts: [{ label: 'Head + members', x: 28, y: 36 }, { label: 'Admin transfer', x: 74, y: 58 }], frame: 'collab' },
  { id: 'analytics', title: 'Analytics is not payroll', icon: '📊', blurb: 'Charts by trade, op code, shift. Date range. Not a submit button.', callouts: [{ label: 'By trade / code', x: 28, y: 36 }, { label: 'Not payroll', x: 74, y: 58 }], frame: 'analytics' },
  { id: 'troubleshoot', title: 'When something is wrong', icon: '🛠️', blurb: 'Empty dropdown, inactive code, period lock, or employee not active on the master.', callouts: [{ label: 'Empty list', x: 24, y: 28 }, { label: 'Active-only picker', x: 74, y: 58 }], frame: 'ttAdmin' },
  { id: 'legend', title: 'Column legend', icon: '📖', blurb: 'Reg / T12 / DBL / Height are hours. R&B, meals, travel$, bus are allowances. Shifts D/A/N.', callouts: [{ label: 'Hours columns', x: 26, y: 30 }, { label: 'Allowances', x: 74, y: 58 }], frame: 'ttHero' },
];

/** Curated scenes for Time Tracker Admin (1:1 with time-tracker-admin-help narration). */
export const TIME_TRACKER_ADMIN_SCENES = [
  { id: 'audience', title: 'Who Admin is for', icon: '🛡️', blurb: 'Cost clerk, controller, superadmin. Writes return 403 otherwise.', callouts: [{ label: 'canManageTimeTrackerAdmin', x: 30, y: 36 }, { label: '403 on writes', x: 74, y: 58 }], frame: 'ttAdmin' },
  { id: 'six-cards', title: 'The six cards', icon: '🗂️', blurb: 'Op Codes, Employees, Extras, Reports, Period Lock, Maestro Export.', callouts: [{ label: 'Op Codes · Extras', x: 24, y: 28 }, { label: 'Lock · Maestro', x: 74, y: 58 }], frame: 'ttAdmin' },
  { id: 'job-disc-admin', title: 'Job plus discipline is the rule', icon: '🧭', blurb: 'Search the job number, pick the job, then the discipline. That is how extras are stored.', callouts: [{ label: 'Search 30437', x: 24, y: 28 }, { label: 'Then PIP', x: 74, y: 58 }], frame: 'ttDropdown' },
  { id: 'opcodes', title: 'Op Codes management', icon: '📋', blurb: 'Project-discipline, numeric ≤5 digits, Excel, active toggle.', callouts: [{ label: 'Add / import', x: 24, y: 28 }, { label: 'Green vs gray', x: 74, y: 58 }], frame: 'ttAdmin' },
  { id: 'extras', title: 'Extras from Change Log', icon: '🔶', blurb: 'Change Log writes extra#. This screen enables or disables. Amber badge.', callouts: [{ label: 'Change Log badge', x: 26, y: 28 }, { label: 'Toggle only', x: 74, y: 58 }], frame: 'ttAdmin' },
  { id: 'employees', title: 'Employees and the three counts', icon: '👥', blurb: '1383 master vs 227 Entra vs 200 unmatched actives. Do not subtract them.', callouts: [{ label: '1383 master', x: 20, y: 28 }, { label: '227 Entra', x: 50, y: 28 }, { label: '200 unmatched', x: 80, y: 28 }], frame: 'ttHero' },
  { id: 'placeholders', title: 'Placeholder codes', icon: '🔢', blurb: '1111 Need, 9999 New, 9993–9998 Need-disc-role. 0000 forbidden.', callouts: [{ label: '1111 / 9999', x: 26, y: 36 }, { label: '0000 forbidden', x: 74, y: 58 }], frame: 'form' },
  { id: 'discipline', title: 'Discipline equals trade', icon: '🔧', blurb: 'One field: discipline_code. Legacy trade column is not shown.', callouts: [{ label: 'PIP MWR ELE…', x: 28, y: 36 }, { label: 'Not two fields', x: 74, y: 58 }], frame: 'ttPipeline' },
  { id: 'entra', title: 'Entra sync is email only', icon: '☁️', blurb: 'Match by Employee ID or email. Never invent emails. Does not filter the roster.', callouts: [{ label: 'Sync Now', x: 24, y: 28 }, { label: 'Email / portal only', x: 74, y: 58 }], frame: 'collab' },
  { id: 'hiring', title: 'Hiring and layoff path', icon: '🔁', blurb: 'Tanya → Jordan Entra → portal → Time Tracker. Active / Leave / Terminated.', callouts: [{ label: 'Master first', x: 24, y: 28 }, { label: 'Then Entra', x: 74, y: 58 }], frame: 'ttPipeline' },
  { id: 'crews-admin', title: 'Crew management', icon: '👷', blurb: 'Create, head, members, projects, fab-shop multi-project, transfer.', callouts: [{ label: 'Head + members', x: 26, y: 36 }, { label: 'Daily depends on this', x: 74, y: 58 }], frame: 'collab' },
  { id: 'lock', title: 'Payroll period lock', icon: '🔒', blurb: 'Create CA/US periods. Lock/unlock/delete. Blocks field edits with 423.', callouts: [{ label: 'CA vs US', x: 24, y: 28 }, { label: 'Lock after submit', x: 74, y: 58 }], frame: 'freeze' },
  { id: 'maestro', title: 'Maestro payroll export', icon: '📤', blurb: 'Week + project. Nine-line expansion. Submitted hours only.', callouts: [{ label: '9 Maestro lines', x: 28, y: 36 }, { label: 'No drafts', x: 74, y: 58 }], frame: 'io' },
  { id: 'pdfs', title: 'P D F reports', icon: '📄', blurb: 'Details, Master Payroll, OP Code Summary, Contract. CA vs US day order.', callouts: [{ label: 'Four packs', x: 26, y: 28 }, { label: 'Comments = notes', x: 74, y: 58 }], frame: 'io' },
  { id: 'analytics-admin', title: 'Analytics before you lock', icon: '📉', blurb: 'Hours by trade, op, shift. Last look before lock and export.', callouts: [{ label: 'By trade / code', x: 28, y: 36 }, { label: 'Fix while open', x: 74, y: 58 }], frame: 'analytics' },
  { id: 'checklist', title: 'End-to-end payroll checklist', icon: '✅', blurb: 'Load codes → extras → crews → open period → submit → lock → Maestro → PDFs.', callouts: [{ label: 'Load then enter', x: 24, y: 28 }, { label: 'Lock then export', x: 74, y: 58 }], frame: 'checklist' },
  { id: 'admin-fix', title: 'Admin troubleshooting', icon: '🛠️', blurb: 'Empty dropdown, duplicate UID, export 403, empty report, Entra mismatch.', callouts: [{ label: 'Empty list', x: 24, y: 28 }, { label: '200 unmatched', x: 74, y: 58 }], frame: 'ttAdmin' },
  { id: 'audit', title: 'Audit log', icon: '🧾', blurb: 'Accounting and superadmin. Who changed what. Restore removed rows. CSV export.', callouts: [{ label: 'Orange Audit', x: 24, y: 28 }, { label: 'Restore', x: 74, y: 58 }], frame: 'ttAdmin' },
  { id: 'soft-delete', title: 'Nothing is erased', icon: '♻️', blurb: 'Soft delete only. Timesheets, codes, extras, crews, periods stay and can be restored.', callouts: [{ label: 'Removed, not gone', x: 24, y: 28 }, { label: 'Restore from Audit', x: 74, y: 58 }], frame: 'freeze' },
  { id: 'backups', title: 'What keeps the tool standing', icon: '🛟', blurb: 'Azure Postgres backups, soft delete, audit, period lock, dev-first migrations.', callouts: [{ label: 'Azure PITR', x: 24, y: 28 }, { label: 'Lock + audit', x: 74, y: 58 }], frame: 'ttPipeline' },
];

/**
 * Optional hand-authored tours keyed by guideId.
 * Shape: { title?, accent?, scenes }
 */
/** Curated scenes for the Corporation Resource Histogram (matches fab-histogram-help narration). */
export const FAB_HISTOGRAM_SCENES = [
  {
    id: 'shop-too-full',
    title: 'Is the shop too full?',
    icon: '🏭',
    blurb: 'Coloured stacks are leftover hours. The dark line is shop capacity. Over the line means that week is too full.',
    callouts: [
      { label: 'Legend on the left', x: 16, y: 42 },
      { label: 'Capacity line', x: 62, y: 28 },
    ],
    frame: 'fabHero',
  },
  {
    id: 'hours-move',
    title: 'When the hours move',
    icon: '📂',
    blurb: 'Import the Data Dump workbooks. The chart reloads when the upload finishes. Export is one file you edit and Import back.',
    callouts: [
      { label: 'Import', x: 82, y: 12 },
      { label: 'Sheet1 Remaining Units', x: 50, y: 52 },
    ],
    frame: 'fabReload',
  },
  {
    id: 'crew-week',
    title: 'Crew and the shop week',
    icon: '⚙️',
    blurb: 'Planning holds men, hours per week, and absences. Bars stay planned hours. Only the line and man-weeks move.',
    callouts: [
      { label: 'Men per shop', x: 28, y: 40 },
      { label: '32 h/wk · 8% absences', x: 72, y: 36 },
    ],
    frame: 'fabPlanning',
  },
  {
    id: 'page-quiet',
    title: 'Keep the page quiet',
    icon: '📊',
    blurb: 'Filters, KPIs, and Planning sit on the top line. Audit Log is next to Help. Default units are man-weeks.',
    callouts: [
      { label: 'Man-weeks is the default', x: 38, y: 22 },
      { label: 'Click a job to spotlight', x: 16, y: 55 },
    ],
    frame: 'fabFilters',
  },
  {
    id: 'many-jobs',
    title: 'Many jobs, one curve',
    icon: '📁',
    blurb: 'Discipline starts on Fabrication. Clear it for every trade. A program is jobs (Goodyear). A portfolio is programs (Canada). Define them from P6 XERs.',
    callouts: [
      { label: 'Portfolio · Goodyear', x: 28, y: 20 },
      { label: 'Define once', x: 62, y: 20 },
    ],
    frame: 'fabPortfolio',
  },
  {
    id: 'comes-next',
    title: 'What comes next',
    icon: '✅',
    blurb: 'P6 is the opening source. Excel dumps are still there for shop-file edits. Portal cost breakdowns will feed the same chart later.',
    callouts: [
      { label: 'P6 · default', x: 26, y: 48 },
      { label: 'Cost breakdown · soon', x: 74, y: 48 },
    ],
    frame: 'fabNext',
  },
];

export const HELP_TOUR_OVERRIDES = {
  [PIPING_WALKTHROUGH_GUIDE_ID]: {
    title: 'Piping Progress Tracker — New Employee Tour',
    accent: PIPING_ACCENT,
    scenes: PIPING_WALKTHROUGH_SCENES,
  },
  [FAB_HISTOGRAM_GUIDE_ID]: {
    title: 'Corporation Resource Histogram — Video Tour',
    accent: FAB_ACCENT,
    scenes: FAB_HISTOGRAM_SCENES,
  },
  [GA_DASHBOARDS_GUIDE_ID]: {
    title: 'GA Dashboards — Video Tour',
    accent: GA_ACCENT,
    scenes: GA_DASHBOARDS_SCENES,
  },
  [TIME_TRACKER_GUIDE_ID]: {
    title: 'Time Tracker — Field Tour',
    accent: TT_ACCENT,
    scenes: TIME_TRACKER_SCENES,
  },
  [TIME_TRACKER_ADMIN_GUIDE_ID]: {
    title: 'Time Tracker — Admin Tour',
    accent: TT_ADMIN_ACCENT,
    scenes: TIME_TRACKER_ADMIN_SCENES,
  },
};

/**
 * Resolve a tour for a guideId.
 * @returns {{ guideId, title, accent, scenes } | null}
 */
export function getHelpTour(guideId, options = {}) {
  if (!guideId) return null;

  const override = HELP_TOUR_OVERRIDES[guideId];
  const narration = getHelpNarration(guideId);

  if (override?.scenes?.length) {
    return {
      guideId,
      title: options.title || override.title || guideId,
      accent: options.accent || override.accent || '#0369a1',
      scenes: override.scenes,
    };
  }

  if (!narration?.length) return null;

  const scenes = buildScenesFromNarration(guideId, options);
  if (!scenes.length) return null;

  return {
    guideId,
    title: options.title || guideId,
    accent: options.accent || '#0369a1',
    scenes,
  };
}

/** All guideIds that can produce a tour (override or narration). */
export function listHelpTourIds() {
  const ids = new Set([
    ...Object.keys(HELP_TOUR_OVERRIDES),
    ...Object.keys(HELP_NARRATIONS || {}),
  ]);
  return [...ids].filter((id) => {
    const tour = getHelpTour(id);
    return tour && tour.scenes?.length;
  });
}

/** Display metadata for Learning Center / Help Center tour lists. */
export const HELP_TOUR_CATALOG = {
  'piping-progress-walkthrough': { title: 'Piping Progress Tracker — New Employee Tour', accent: '#0369a1', group: 'Operations' },
  'discipline-matrix-help': { title: 'Discipline Progress Tracker', accent: '#0369a1', group: 'Operations' },
  'pipe-testing-tracker-help': { title: 'Piping Testing Tracker', accent: '#0f766e', group: 'Operations' },
  'rfi-log-help': { title: 'RFI Log', accent: '#0d6efd', group: 'Operations' },
  'sheets-viewer-help': { title: 'Sheets Viewer', accent: '#0369a1', group: 'Operations' },
  'change-log-help': { title: 'Change Log / Control Chart', accent: '#dc2626', group: 'Operations' },
  'indirect-resources-help': { title: 'Indirect Resources', accent: '#0d6efd', group: 'Operations' },
  'fab-histogram-help': { title: 'Corporation Resource Histogram', accent: '#0f766e', group: 'Operations' },
  'material-management-help': { title: 'Material Management', accent: '#2563eb', group: 'Operations' },
  'schedule-gantt-help': { title: 'Schedule (Gantt)', accent: '#0369a1', group: 'Schedule' },
  'schedule-checklists-help': { title: 'Schedule Checklists', accent: '#0f766e', group: 'Schedule' },
  'schedule-model-help': { title: 'Schedule Model', accent: '#0f766e', group: 'Schedule' },
  'schedule-analysis-help': { title: 'Schedule Analysis', accent: '#0f766e', group: 'Schedule' },
  'ga-dashboards-help': { title: 'GA Dashboards', accent: '#0f766e', group: 'Schedule' },
  'areas-of-concern-help': { title: 'Areas of Concern', accent: '#0f766e', group: 'Schedule' },
  'press-progress-help': { title: 'Press Progress', accent: '#0f766e', group: 'Schedule' },
  'proposal-schedules-help': { title: 'Proposal Schedules', accent: '#0f766e', group: 'Schedule' },
  'schedule-msp-export-help': { title: 'MS Project Export', accent: '#0f766e', group: 'Schedule' },
  'master-component-list': { title: 'Master Component List', accent: '#0f766e', group: 'Schedule' },
  'evm-report-help': { title: 'EVM / Resource Control', accent: '#b91c1c', group: 'Reports' },
  'budget-report-help': { title: 'Budget Report', accent: '#1d4ed8', group: 'Reports' },
  'forecasting-dashboard-help': { title: 'Forecasting Dashboard', accent: '#0369a1', group: 'Reports' },
  'maestro-reports-help': { title: 'Maestro Dashboard', accent: '#0369a1', group: 'Reports' },
  'safety-training-help': { title: 'Safety Training', accent: '#b45309', group: 'Safety' },
  'estimate-management-help': { title: 'Estimate Management', accent: '#0369a1', group: 'Admin' },
  'coding-structure-help': { title: 'Coding Structure', accent: '#dc3545', group: 'Admin' },
  'time-tracker-help': { title: 'Time Tracker — Field Hours', accent: '#0d9488', group: 'Operations' },
  'time-tracker-admin-help': { title: 'Time Tracker — Admin', accent: '#0f766e', group: 'Operations' },
  'hiring-layoff-help': { title: 'Hiring / Layoff', accent: '#1e3a5f', group: 'Admin' },
  'sharepoint-browser-help': { title: 'SharePoint Browser', accent: '#0f766e', group: 'Admin' },
  'autodesk-build-help': { title: 'Autodesk Build', accent: '#0f766e', group: 'Admin' },
  'procore-help': { title: 'Procore', accent: '#ea580c', group: 'Admin' },
  'bid-log-help': { title: 'Bid Log', accent: '#0d6efd', group: 'Sales' },
  'clients-database-help': { title: 'Clients Database', accent: '#0d6efd', group: 'Sales' },
  'selected-contractors-help': { title: 'Selected Contractors', accent: '#17a2b8', group: 'Procurement' },
  'feature-flags-help': { title: 'Feature Flags', accent: '#0f766e', group: 'Dev' },
  'permissions-dashboard-help': { title: 'Permissions Dashboard', accent: '#e74c3c', group: 'Dev' },
  'proposals-dashboard-help': { title: 'Proposals Dashboard', accent: '#0d6efd', group: 'Dev' },
  'help-button-template-help': { title: 'Help Button Template', accent: '#6c757d', group: 'Dev' },
  'faq-help': { title: 'FAQ', accent: '#dc3545', group: 'Help' },
};

/**
 * All playable tours with display metadata for catalogs.
 * @returns {Array<{ guideId, title, accent, scenes, group }>}
 */
export function listHelpTours() {
  return listHelpTourIds()
    .map((id) => {
      const meta = HELP_TOUR_CATALOG[id] || {};
      const title = meta.title || HELP_TOUR_OVERRIDES[id]?.title || id;
      const accent = meta.accent || HELP_TOUR_OVERRIDES[id]?.accent || '#0369a1';
      const tour = getHelpTour(id, { title, accent });
      if (!tour) return null;
      return { ...tour, group: meta.group || 'Other' };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const g = String(a.group).localeCompare(String(b.group));
      return g !== 0 ? g : String(a.title).localeCompare(String(b.title));
    });
}
